import type { JSX } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCandidateWizard } from '../hooks/useCandidateWizard';
import { getWizardStep } from '../types/candidate-wizard.types';
import { CandidateWizardStepper } from './CandidateWizardStepper';
import { CandidateWizardStepCard } from './CandidateWizardStepCard';
import { CandidateExperienceStep } from './CandidateExperienceStep';
import { CandidateReviewStep } from './CandidateReviewStep';
import type { MissingProfileRequirement } from './CandidateReviewStep';
import type { Experience } from '@/features/experiences/types/experience.types';

function ContactsErrorNotice({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-destructive/40 p-3">
      <div className="space-y-1">
        <p className="text-sm text-destructive">Could not load your reference contacts.</p>
        <p className="text-xs text-muted-foreground">Your step progress may be incomplete.</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function getMissingRequirement(
  experiences: Experience[],
  isExperienceComplete: boolean
): MissingProfileRequirement {
  if (experiences.length === 0) return 'experience';
  return isExperienceComplete ? null : 'reference-contact';
}

export function CandidateProfileWizard(): JSX.Element {
  const wizard = useCandidateWizard();
  const { experiences, contactsByExperience, activeStepId, completion } = wizard;

  if (wizard.isLoading) {
    return (
      <Card>
        <CardContent className="p-6">Loading...</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <CandidateWizardStepper
        activeStepId={activeStepId}
        completion={completion}
        onSelect={wizard.goToStep}
      />
      {wizard.contactsError && activeStepId !== 'review' && (
        <ContactsErrorNotice onRetry={wizard.retryContacts} />
      )}
      <CandidateWizardStepCard step={getWizardStep(activeStepId)}>
        {activeStepId === 'experience' && (
          <CandidateExperienceStep
            experiences={experiences}
            contactsByExperience={contactsByExperience}
            loadingExperienceIds={wizard.loadingExperienceIds}
            canContinue={completion.experience}
            onContinue={(): void => wizard.goToStep('review')}
          />
        )}
        {activeStepId === 'review' && wizard.contactsError && (
          <ContactsErrorNotice onRetry={wizard.retryContacts} />
        )}
        {activeStepId === 'review' && !wizard.contactsError && (
          <CandidateReviewStep
            missingRequirement={getMissingRequirement(experiences, completion.experience)}
            onBack={(): void => wizard.goToStep('experience')}
            experiences={experiences}
            contactsByExperience={contactsByExperience}
          />
        )}
      </CandidateWizardStepCard>
    </div>
  );
}
